import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _includes from 'lodash/includes';
import uniqid from 'uniqid';

// constants
import {
  DRAGGABLE_DIRECTIONS,
  DRAGGABLE_DESTINATION_CHARGERS,
  DESTINATION_CHARGER,
  CLICK,
} from '../../lib/Constants';

// components
import { Stopover, Charger, Destination } from '../../components/Directions';

// actions
import { fetchDestinationChargers } from '../../reducers/DestinationChargers/actions';

// helpers
import DateTimeUtils from '../../lib/DateTimeUtils';
import Enliven from '../../lib/Enliven';
import { formatDistance, localizeDistance } from '../../lib/ConversionUtils';
import MapUtils from '../../lib/MapUtils';
import MapWindow from '../../lib/MapWindow';
import AnalyticsUtils from '../../lib/AnalyticsUtils';

const ListPropTypes = {
  directions: PropTypes.arrayOf(PropTypes.object).isRequired, // Directions received from routing service
  buttonLabel: PropTypes.string.isRequired, // Label for destination charger toggle
  chargingLabel: PropTypes.string.isRequired, // Label for "charging" time of trip stopover
  isDestinationChargers: PropTypes.bool.isRequired, // True when the trip has destination chargers
  hoursLabel: PropTypes.string.isRequired, // Hours only label for "Duration" of trip
  minutesLabel: PropTypes.string.isRequired, // Minutes only label for "Duration" of trip
  hoursMinutesLabel: PropTypes.string.isRequired, // Hours & minutes only label for "Duration" of trip
  locale: PropTypes.string.isRequired, // locale code eg 'en_US'
  unit: PropTypes.shape({}), // Object of metric and imperial units
  isMobile: PropTypes.bool.isRequired, // Is this a mobile device
  fields: PropTypes.arrayOf(PropTypes.object).isRequired, // Contains objects with information about each input field & location
  showDestinationChargers: PropTypes.bool.isRequired, // True if destination chargers are displayed
  fieldsLength: PropTypes.number.isRequired, // Number of places/fields in a users trip
  dispatchFetchDestinationChargers: PropTypes.func.isRequired, // Fetches destination chargers
};

const ListDefaultProps = {
  unit: {},
};

class List extends Component {
  static openChargerWindow(charger) {
    // prevent charger window from opening when directions component is in initial position
    const animation = Enliven.currentAnimation[DRAGGABLE_DIRECTIONS];
    if (animation === Enliven.SLIDE_DOWN || animation === Enliven.SLIDE_UP) {
      return;
    }

    MapWindow.handleOpenChargerWindow(charger);
  }

  constructor(props) {
    super(props);

    this.getDuration = this.getDuration.bind(this);
    this.renderDirectionsList = this.renderDirectionsList.bind(this);
    this.showNearbyDestinationChargers = this.showNearbyDestinationChargers.bind(this);
  }

  getDuration({ duration, distance }) {
    const {
      hoursLabel,
      minutesLabel,
      hoursMinutesLabel,
      locale,
      unit,
    } = this.props;
    const labels = { hoursLabel, minutesLabel, hoursMinutesLabel };
    const params = { labels, seconds: duration };
    const { label, hours, minutes } = DateTimeUtils.destructTimeAndLabel(
      params,
    );

    const formattedDistance = formatDistance(
      localizeDistance(distance, unit, 'imperial'),
      unit,
      locale,
    );
    // eslint-disable-next-line object-curly-newline
    return { label, hours, minutes, distance: formattedDistance };
  }

  /**
   * @description Fetches and displays destination chargers
   *
   * @returns {void}
   */
  showNearbyDestinationChargers() {
    const {
      dispatchFetchDestinationChargers,
      showDestinationChargers,
      fields,
      fieldsLength,
      isMobile,
    } = this.props;

    // fetch destination chargers
    dispatchFetchDestinationChargers(!showDestinationChargers);

    // pan to destination marker
    const coords = MapUtils.latLng(
      fields[fieldsLength - 1].lat,
      fields[fieldsLength - 1].lng,
    );
    MapUtils.panToMapMarker(coords);

    // animate the draggable destination chargers component on mobile devices
    if (isMobile) {
      const DDYPosition = Enliven.lastY[DRAGGABLE_DIRECTIONS];
      Enliven.animate(
        DRAGGABLE_DIRECTIONS,
        'directions-slide-left',
        0,
        '-100%',
        DDYPosition,
        DDYPosition,
        'up',
      );

      const DDCYPosition = Enliven.getTranslateY(
        DRAGGABLE_DESTINATION_CHARGERS,
      );
      Enliven.animate(
        DRAGGABLE_DESTINATION_CHARGERS,
        'destination-slide-left',
        0,
        '-100%',
        DDCYPosition,
        DDCYPosition,
        'up',
      );
    }

    // Analytics
    AnalyticsUtils.pushInteraction({
      interaction: DESTINATION_CHARGER,
      label: CLICK,
    });
  }

  renderChargerComponent(charger) {
    const { chargingLabel } = this.props;

    // Round to nearest 5 minute interval, with a min charging period of 5 minutes
    const interval = 5;
    let minutes = DateTimeUtils.secondsToMinutes(
      charger.charge_dur_s,
    ).toString();
    minutes = DateTimeUtils.roundToNearest({
      time: minutes,
      min: interval,
      interval,
    });

    return (
      <Charger
        key={uniqid()}
        icon="supercharger-pin"
        title={charger.name}
        duration={minutes}
        label={chargingLabel}
        handleClick={() => this.constructor.openChargerWindow(charger)}
        type="super"
      />
    );
  }

  renderDestinationComponent(item) {
    const { buttonLabel, isDestinationChargers } = this.props;
    const duration = this.getDuration(item);

    return (
      <Destination
        key={uniqid()}
        address={item.address}
        durationLabel={duration.label}
        hours={duration.hours}
        minutes={duration.minutes > 0 ? duration.minutes : 0}
        distance={duration.distance}
        handleClick={this.showNearbyDestinationChargers}
        buttonText={buttonLabel}
        isDestinationChargers={isDestinationChargers}
      />
    );
  }

  renderDirectionsList() {
    const { directions } = this.props;

    // eslint-disable-next-line arrow-parens
    return directions.map(item => {
      let component = null;

      if (_includes(['origin', 'stopover'], item.type)) {
        component = (
          <Stopover
            key={uniqid()}
            icon="circle-o"
            address={item.address || item.addr}
          />
        );
      }

      if (item.type === 'charger') {
        component = this.renderChargerComponent(item);
      }

      if (item.type === 'destination') {
        component = this.renderDestinationComponent(item);
      }

      return component;
    });
  }

  render() {
    return <div>{this.renderDirectionsList()}</div>;
  }
}

List.propTypes = ListPropTypes;

List.defaultProps = ListDefaultProps;

const mapStateToProps = state => ({
  directions: state.Directions.directions,
  buttonLabel: state.Directions.button.label,
  chargingLabel: state.Directions.charging.label,
  isDestinationChargers: state.DestinationChargers.isDestinationChargers,
  hoursLabel: state.Directions.duration.hoursLabel,
  minutesLabel: state.Directions.duration.minutesLabel,
  hoursMinutesLabel: state.Directions.duration.hoursMinutesLabel,
  unit: state.App.unit,
  locale: state.App.locale,
  isMobile: state.App.isMobile,
  fields: state.Places.fields,
  showDestinationChargers: state.DestinationChargers.showDestinationChargers,
  fieldsLength: state.Places.fieldsLength,
});

const mapDispatchToProps = dispatch => ({
  // eslint-disable-next-line arrow-parens
  dispatchFetchDestinationChargers: showDestinationChargers => {
    dispatch(fetchDestinationChargers(showDestinationChargers));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(List);
